import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type OrderChatSenderRole =
  | 'customer'
  | 'provider'
  | 'company'
  | 'admin'
  | 'staff';

export interface OrderChatMessage {
  id?: string;
  text: string;
  senderId: string;
  senderName: string;
  senderRole: OrderChatSenderRole;
  createdAt: number | object | null;
}

export interface OrderChatMeta {
  orderId: string;
  customerId: string;
  providerId: string;
  companyId: string | null;
  status: 'open' | 'closed';
  updatedAt: number | object | null;
}

export interface OrderChatUserBrief {
  uid: string;
  name: string;
  email: string;
  phone: string;
  imageUrl: string;
  role: string;
  roleLabel: string;
}

@Injectable({ providedIn: 'root' })
export class OrderChatService {
  private readonly backendBase =
    environment.aunakBackendUrl ||
    'https://squid-app-4ndr5.ondigitalocean.app';

  constructor(
    private db: AngularFireDatabase,
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private http: HttpClient
  ) {}

  private messagesPath(orderId: string): string {
    return `orderChats/${orderId}/messages`;
  }

  private metaPath(orderId: string): string {
    return `orderChats/${orderId}/meta`;
  }

  watchMessages(orderId: string): Observable<OrderChatMessage[]> {
    if (!orderId) return of([]);
    return this.db
      .list<OrderChatMessage>(this.messagesPath(orderId), (ref) =>
        ref.orderByChild('createdAt').limitToLast(200)
      )
      .snapshotChanges()
      .pipe(
        map((snaps) =>
          snaps.map((s) => {
            const val = s.payload.val() || ({} as OrderChatMessage);
            return { ...val, id: s.key || undefined };
          })
        )
      );
  }

  watchMeta(orderId: string): Observable<OrderChatMeta | null> {
    if (!orderId) return of(null);
    return this.db
      .object<OrderChatMeta>(this.metaPath(orderId))
      .valueChanges()
      .pipe(map((v) => v || null));
  }

  async requestStaffAccess(orderId: string): Promise<void> {
    const id = String(orderId || '').trim();
    if (!id) return;
    const user = await this.afAuth.currentUser;
    if (!user) return;
    const idToken = await user.getIdToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    });
    await this.http
      .post(
        `${this.backendBase}/order-chat/access`,
        { orderId: id },
        { headers }
      )
      .toPromise();
  }

  formatUserDisplayName(d: Record<string, any> | null | undefined, fallback = ''): string {
    if (!d) return fallback;
    const full = String(d['name'] || d['displayName'] || '').trim();
    if (full && full.toLowerCase() !== 'user') return full;
    const composed = [d['fname'], d['lname']]
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .join(' ');
    if (composed) return composed;
    const email = String(d['email'] || '').trim();
    if (email) return email.split('@')[0];
    return fallback || 'Unknown';
  }

  formatUserPhone(d: Record<string, any> | null | undefined): string {
    if (!d) return '';
    const direct = String(d['phone'] || d['mobile'] || '').trim();
    if (direct) return direct;
    const code = String(d['phoneCode'] || '').trim();
    const tlf = String(d['tlf'] || d['phoneNumber'] || '').trim();
    return `${code}${tlf ? (code ? ' ' : '') + tlf : ''}`.trim();
  }

  formatUserImage(d: Record<string, any> | null | undefined): string {
    if (!d) return '';
    return String(
      d['photoURL'] ||
        d['profileImage'] ||
        d['logo'] ||
        d['image'] ||
        d['avatar'] ||
        ''
    ).trim();
  }

  roleLabel(rawRole: string): string {
    const r = String(rawRole || '').toLowerCase();
    if (r === 'customer') return 'Customer';
    if (r === 'solosp') return 'Solo provider';
    if (r === 'cprovider') return 'Company provider';
    if (r === 'company') return 'Company';
    if (r === 'admin' || r === 'superadmin' || r === 'super_admin') return 'Admin';
    if (r === 'provider') return 'Provider';
    return rawRole ? String(rawRole) : 'User';
  }

  async loadUserBrief(uid: string): Promise<OrderChatUserBrief | null> {
    const id = String(uid || '').trim();
    if (!id) return null;
    const snap = await this.afs.collection('users').doc(id).ref.get();
    if (!snap.exists) {
      return {
        uid: id,
        name: 'Unknown user',
        email: '',
        phone: '',
        imageUrl: '',
        role: '',
        roleLabel: 'User',
      };
    }
    const d = (snap.data() || {}) as Record<string, any>;
    const role = String(d['role'] || '').toLowerCase();
    return {
      uid: id,
      name: this.formatUserDisplayName(d, 'Unknown user'),
      email: String(d['email'] || '').trim(),
      phone: this.formatUserPhone(d),
      imageUrl: this.formatUserImage(d),
      role,
      roleLabel: this.roleLabel(role),
    };
  }

  adminProfilePath(brief: OrderChatUserBrief): string {
    const r = brief.role;
    if (r === 'customer') return `/users/customer/${brief.uid}`;
    if (r === 'solosp') return `/users/solosp/${brief.uid}`;
    if (r === 'company') return `/users/company/${brief.uid}`;
    return `/users/${brief.uid}`;
  }

  async resolveSenderProfile(): Promise<{
    uid: string;
    name: string;
    role: OrderChatSenderRole;
  }> {
    const user = await this.afAuth.currentUser;
    if (!user?.uid) throw new Error('Not signed in');
    const snap = await this.afs.collection('users').doc(user.uid).ref.get();
    const d = (snap.data() || {}) as Record<string, any>;
    const rawRole = String(d['role'] || '').toLowerCase();
    let role: OrderChatSenderRole = 'customer';
    if (rawRole === 'solosp' || rawRole === 'cprovider') role = 'provider';
    else if (rawRole === 'company') role = 'company';
    else if (
      rawRole === 'admin' ||
      rawRole === 'superadmin' ||
      rawRole === 'super_admin' ||
      rawRole === 'manager' ||
      rawRole === 'support' ||
      rawRole === 'accountant'
    ) {
      role = 'admin';
    }

    const name = this.formatUserDisplayName(
      d,
      String(user.displayName || '').trim() || 'Staff'
    );
    return { uid: user.uid, name, role };
  }
}
